/* eslint-disable no-nested-ternary */
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { ReactComponent as PhotoPlaceholder } from "../../../assets/ic_photo_placeholder.svg";
import { FlowTypes } from "../../../entities/flow-type";

// Conformity ids come from the backend `Conformity` entity:
//   0 = NOT_OK, 1 = OK, 2 = SCHEDULED, 3 = NOT_APPLICABLE
function conformityClass(id) {
  if (id === 1) return "text-emerald-600 dark:text-emerald-400";
  if (id === 2) return "text-sky-600 dark:text-sky-400";
  if (id === 3) return "text-muted-foreground";
  return "text-destructive";
}

function conformityText(id, t) {
  if (id === 1) return t("inspections.items.yes");
  if (id === 2) return t("inspections.items.scheduled");
  if (id === 3) return t("inspections.items.na", "N/A");
  return t("inspections.items.no");
}

function severityBadge(severity, t) {
  if (severity === "high") {
    return {
      label: t("inspections.items.severity-high", "High"),
      className:
        "border-destructive/40 bg-destructive/10 text-destructive",
    };
  }
  if (severity === "medium") {
    return {
      label: t("inspections.items.severity-medium", "Medium"),
      className:
        "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
    };
  }
  return {
    label: t("inspections.items.severity-low", "Low"),
    className: "border-border bg-muted text-muted-foreground",
  };
}

function renderConformityWithSeverity(conformity, severity, t) {
  if (conformity === undefined || conformity === null) return null;
  const showSeverity = conformity === 0 && severity;
  const sev = showSeverity ? severityBadge(severity, t) : null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={cn("font-medium", conformityClass(conformity))}>
        {conformityText(conformity, t)}
      </span>
      {sev ? (
        <Badge variant="outline" className={cn("h-5 px-1.5 text-[10px]", sev.className)}>
          {sev.label}
        </Badge>
      ) : null}
    </div>
  );
}

function photoCell(val, onImageClick) {
  if (!val || val.length === 0) {
    return <PhotoPlaceholder width={28} className="ml-2.5" />;
  }
  return (
    <div
      role="button"
      tabIndex={0}
      className="inline-block size-12 cursor-pointer overflow-hidden rounded-md"
      onClick={() => onImageClick?.(val)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onImageClick?.(val);
        }
      }}
      aria-label="View image gallery"
    >
      <img
        src={val[0]}
        alt="Item thumbnail"
        className="size-full rounded-md object-cover"
      />
    </div>
  );
}

function attachmentsCell({ count, hasMedia, target, t, onSee }) {
  if (count === 0) {
    return (
      <span className="text-muted-foreground">
        {t("inspections.items.non-attachment")}
      </span>
    );
  }
  const label =
    count === 1
      ? t("inspections.items.attachment", { number: count })
      : t("inspections.items.attachments", { number: count });
  return (
    <button
      type="button"
      onClick={() => hasMedia && onSee(target)}
      className="text-primary hover:underline"
    >
      {label}
    </button>
  );
}

export default function generate({
  t,
  onSee,
  controlFlowType = { name: FlowTypes.SEQUENTIAL },
  onImageClick,
}) {
  return controlFlowType.name === FlowTypes.SEQUENTIAL
    ? [
        {
          name: "Item.photos",
          label: t("items.items.icon"),
          options: {
            customBodyRender: (val) => photoCell(val, onImageClick),
          },
        },
        {
          name: "Item.title",
          label: t("items.items.identifier"),
        },
        {
          name: "conformity.id",
          label: t("inspections.info-card.complaint"),
          options: {
            customBodyRender: (val, item) => {
              const severity = item.value?.severity;
              return renderConformityWithSeverity(val, severity, t);
            },
          },
        },
        {
          name: "description",
          label: t("items.items.description"),
          options: {
            customBodyRender: (val) => (
              <div className="max-h-[180px] overflow-y-auto whitespace-pre-wrap">
                {val ?? ""}
              </div>
            ),
          },
        },
        {
          name: "attachments",
          label: t("inspections.items.attachments", { number: "" }),
          options: {
            customBodyRender: (_val, item) => {
              const attachments = item.value?.attachments;
              const legacy = item.value?.photos ?? [];
              const count = attachments?.length ?? legacy.length;
              const target = attachments ?? legacy;
              return attachmentsCell({
                count,
                hasMedia: count > 0,
                target,
                t,
                onSee,
              });
            },
          },
        },
      ]
    : [
        {
          name: "photos",
          label: t("items.items.icon"),
          options: {
            customBodyRender: (val) => photoCell(val, onImageClick),
          },
        },
        {
          name: "title",
          label: t("items.items.identifier"),
        },
        {
          name: "report.conformity.id",
          label: t("inspections.info-card.complaint"),
          options: {
            customBodyRender: (_val, item) => {
              const conformity = item.value.report?.conformity?.id;
              const severity = item.value.report?.severity;
              return renderConformityWithSeverity(conformity, severity, t);
            },
          },
        },
        {
          name: "report.description",
          label: t("items.items.description"),
          options: {
            customBodyRender: (_val, item) => (
              <div className="max-h-[180px] overflow-y-auto whitespace-pre-wrap">
                {item.value.report?.description ?? ""}
              </div>
            ),
          },
        },
        {
          name: "report.attachments",
          label: t("inspections.items.attachments", { number: "" }),
          options: {
            customBodyRender: (_val, item) => {
              const attachments = item.value.report?.attachments;
              const legacy = item.value.report?.photos ?? [];
              const count = attachments?.length ?? legacy.length;
              const target = attachments ?? legacy;
              return attachmentsCell({
                count,
                hasMedia: count > 0,
                target,
                t,
                onSee,
              });
            },
          },
        },
      ];
}
